import React, { useEffect, useRef, useState } from 'react';
import { StyledTextField } from '../productInformation';
import NumberInput from '../../../../components/esg/Inputs/number';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import HelpIcon from '@mui/icons-material/Help';
import {
  DATA_COLLECTION_PERIOD_MAX_LENGTH,
  FUNCTIONAL_UNIT_MAX_LENGTH,
} from '../../constants';
import { useProductFieldError } from '../validation';
import { buildSelectInputWithInfo } from '../fieldInfo';
import {
  isValidDecimalInput,
  normalizeDecimalSeparator,
} from 'src/Lca/utils/number';
import { ComplianceField } from 'src/Lca/compliance';

interface Props {
  values: {
    regulatory_frameworks: string;
    functional_unit: string;
    data_collection_period: string;
    weight: number | null;
    weight_unit: string;
    dimensions: string;
    dimensions_unit: string;
    volume: number | null;
    volume_unit: string;
    product_type: string;
  };
  setValues: Function;
}

const weight_unit = [{ name: 'g' }, { name: 'kg' }, { name: 't' }];
const dimensions_unit = [{ name: 'mm' }, { name: 'cm' }, { name: 'm' }];
const volume_unit = [
  { label: 'm3', value: 'm_3' },
  { label: 'L', value: 'l' },
];

type DateRangeValues = {
  from: string;
  to: string;
};

type DimensionsValues = {
  length: string;
  width: string;
  height: string;
};

const emptyDimensions: DimensionsValues = {
  length: '',
  width: '',
  height: '',
};

const parseDateRange = (rawValue: string): DateRangeValues => {
  const fallback = { from: '', to: '' };
  if (!rawValue) {
    return fallback;
  }

  const directMatch = rawValue.match(
    /(\d{4}-\d{2}-\d{2})\s*(?:to|-|\/)\s*(\d{4}-\d{2}-\d{2})/i
  );
  if (directMatch) {
    return { from: directMatch[1], to: directMatch[2] };
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue.trim())) {
    return { from: rawValue.trim(), to: '' };
  }

  return fallback;
};

const toDateRange = (values: DateRangeValues) => {
  if (values.from && values.to) {
    return `${values.from} to ${values.to}`;
  }
  return values.from || values.to || '';
};

const normalizeDimensionSegment = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue || !isValidDecimalInput(trimmedValue)) {
    return '';
  }

  return normalizeDecimalSeparator(trimmedValue);
};

export const parseDimensions = (rawValue: string): DimensionsValues => {
  if (!rawValue) {
    return emptyDimensions;
  }

  const trimmedValue = rawValue.trim();
  const rawSegments = rawValue.split(/\s*x\s*/i).slice(0, 3);
  const hasDimensionSeparators = rawSegments.length > 1;
  const hasOnlyValidSegments = rawSegments.every(
    (segment) => segment === '' || isValidDecimalInput(segment)
  );

  if (hasDimensionSeparators && hasOnlyValidSegments) {
    return {
      length: normalizeDimensionSegment(rawSegments[0] || ''),
      width: normalizeDimensionSegment(rawSegments[1] || ''),
      height: normalizeDimensionSegment(rawSegments[2] || ''),
    };
  }

  if (isValidDecimalInput(trimmedValue)) {
    return {
      ...emptyDimensions,
      length: normalizeDimensionSegment(trimmedValue),
    };
  }

  const numbers = rawValue.match(/(?:\d+(?:[.,]\d*)?|[.,]\d*)/g) || [];
  if (numbers.length === 0) {
    return emptyDimensions;
  }

  return {
    length: (numbers[0] || '').replace(',', '.'),
    width: (numbers[1] || '').replace(',', '.'),
    height: (numbers[2] || '').replace(',', '.'),
  };
};

export const toDimensions = (values: DimensionsValues) => {
  const normalizedValues = [values.length, values.width, values.height].map(
    (value) => normalizeDimensionSegment(value)
  );

  let lastFilledIndex = normalizedValues.length - 1;
  while (lastFilledIndex >= 0 && !normalizedValues[lastFilledIndex]) {
    lastFilledIndex -= 1;
  }

  if (lastFilledIndex < 0) {
    return '';
  }

  return normalizedValues.slice(0, lastFilledIndex + 1).join(' x ');
};

export default function ProductCharacteristics({ setValues, values }: Props) {
  const { t, i18n } = useTranslation('lca', {
    keyPrefix: 'productCharacteristics',
  });
  const isServiceProduct = values.product_type?.toLowerCase() === 'service';
  const parsedDateRange = parseDateRange(values.data_collection_period || '');
  const parsedDimensions = parseDimensions(values.dimensions || '');
  const [dimensionInputValues, setDimensionInputValues] =
    useState<DimensionsValues>(parsedDimensions);
  const lastSerializedDimensionsRef = useRef(values.dimensions || '');

  const functionalUnitLength = values.functional_unit?.length || 0;
  const isFunctionalUnitTooLong =
    functionalUnitLength > FUNCTIONAL_UNIT_MAX_LENGTH;
  const functionalUnitHelperText = isFunctionalUnitTooLong
    ? t('functionalUnitLimitError', {
        max: FUNCTIONAL_UNIT_MAX_LENGTH,
      })
    : undefined;

  const dataCollectionPeriodLength = values.data_collection_period?.length || 0;
  const isDataCollectionPeriodTooLong =
    dataCollectionPeriodLength > DATA_COLLECTION_PERIOD_MAX_LENGTH;
  const dataCollectionPeriodHelperText = isDataCollectionPeriodTooLong
    ? t('dataCollectionPeriodLimitError', {
        max: DATA_COLLECTION_PERIOD_MAX_LENGTH,
      })
    : undefined;
  const functionalUnitError = useProductFieldError(
    'functional_unit',
    values.functional_unit
  );
  const weightError = useProductFieldError('weight', values.weight);
  const weightUnitError = useProductFieldError(
    'weight_unit',
    values.weight_unit
  );
  const dimensionsError = useProductFieldError('dimensions', values.dimensions);
  const dimensionsUnitError = useProductFieldError(
    'dimensions_unit',
    values.dimensions_unit
  );

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent,
    name: string
  ) => {
    setValues({
      ...values,
      [name]: e.target.value,
    });
  };

  const handleDateRangeChange = (
    name: keyof DateRangeValues,
    value: string
  ) => {
    const merged = {
      ...parsedDateRange,
      [name]: value,
    };

    setValues({
      ...values,
      data_collection_period: toDateRange(merged),
    });
  };

  const handleDimensionsChange = (
    name: keyof DimensionsValues,
    value: string
  ) => {
    if (!isValidDecimalInput(value)) {
      return;
    }

    const nextInputValues = {
      ...dimensionInputValues,
      [name]: value,
    };
    const serializedDimensions = toDimensions({
      length: normalizeDecimalSeparator(nextInputValues.length),
      width: normalizeDecimalSeparator(nextInputValues.width),
      height: normalizeDecimalSeparator(nextInputValues.height),
    });

    setDimensionInputValues(nextInputValues);
    lastSerializedDimensionsRef.current = serializedDimensions;

    setValues({
      ...values,
      dimensions: serializedDimensions,
    });
  };

  useEffect(() => {
    const currentSerializedDimensions = values.dimensions || '';

    if (currentSerializedDimensions === lastSerializedDimensionsRef.current) {
      return;
    }

    setDimensionInputValues(parseDimensions(currentSerializedDimensions));
    lastSerializedDimensionsRef.current = currentSerializedDimensions;
  }, [values.dimensions]);

  const [popupOpen, setPopupOpen] = useState<boolean>(false);

  return (
    <div
      style={{
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
      }}
    >
      <Dialog
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        maxWidth="lg"
      >
        <DialogTitle>
          <Typography variant="h3">
            {t('functionalUnitDialog.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography mb="16px">{t('functionalUnitDialog.desc')}</Typography>
          <Typography variant="h4" mb="16px">
            {t('functionalUnitDialog.whatIsIt')}
          </Typography>
          <Typography
            dangerouslySetInnerHTML={{
              __html: t('functionalUnitDialog.whatIsItDesc'),
            }}
            mb="16px"
          />
          <Typography variant="h4" mb="16px">
            {t('functionalUnitDialog.examples')}
          </Typography>
          <Typography
            dangerouslySetInnerHTML={{
              __html: t('functionalUnitDialog.examplesDesc'),
            }}
          />
        </DialogContent>
      </Dialog>

      <ComplianceField
        excelRow={120}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          showInfoIcon={false}
          fullWidth
          label={
            isServiceProduct
              ? t('functionalServiceUnit')
              : t('functionalUnitDeclaredUnit')
          }
          name="functional_unit"
          value={values.functional_unit}
          onChange={(e) => handleChange(e, 'functional_unit')}
          error={isFunctionalUnitTooLong || !!functionalUnitError}
          helperText={functionalUnitHelperText || functionalUnitError}
          InputProps={{
            endAdornment: (
              <IconButton onClick={() => setPopupOpen(true)}>
                <HelpIcon />
              </IconButton>
            ),
          }}
        />
      </ComplianceField>

      <ComplianceField
        excelRow={121}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          type="date"
          fullWidth
          label={t('dataCollectionPeriodFrom')}
          name="data_collection_period_from"
          value={parsedDateRange.from}
          onChange={(e) => handleDateRangeChange('from', e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </ComplianceField>

      <ComplianceField
        excelRow={122}
        regulatoryFrameworks={values.regulatory_frameworks}
      >
        <StyledTextField
          type="date"
          fullWidth
          label={t('dataCollectionPeriodTo')}
          name="data_collection_period_to"
          value={parsedDateRange.to}
          onChange={(e) => handleDateRangeChange('to', e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={isDataCollectionPeriodTooLong}
          helperText={dataCollectionPeriodHelperText}
        />
      </ComplianceField>

      {!isServiceProduct && (
        <>
          <div
            style={{
              display: 'flex',
              gap: '24px',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', gap: '24px' }}>
              <ComplianceField
                excelRow={123}
                regulatoryFrameworks={values.regulatory_frameworks}
              >
                <NumberInput
                  color="#7E4308"
                  state={values}
                  label={t('productWeight')}
                  name="weight"
                  value={values.weight}
                  setValue={setValues}
                  showInfoIcon
                  infoText={i18n.t('lca:fieldInfoFallback', {
                    field: t('productWeight'),
                  })}
                />
              </ComplianceField>
              <ComplianceField
                excelRow={124}
                regulatoryFrameworks={values.regulatory_frameworks}
              >
                <FormControl fullWidth error={!!weightUnitError}>
                  <InputLabel>{t('weightUnit')}</InputLabel>
                  <Select
                    value={values.weight_unit}
                    label={t('weightUnit')}
                    input={buildSelectInputWithInfo({
                      label: t('weightUnit'),
                      title: i18n.t('lca:fieldInfoFallback', {
                        field: t('weightUnit'),
                      }),
                    })}
                    onChange={(event) => handleChange(event, 'weight_unit')}
                  >
                    {weight_unit.map((el) => (
                      <MenuItem key={el.name} value={el.name}>
                        {el.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </ComplianceField>
            </div>
            {(weightError || weightUnitError) && (
              <FormHelperText error>
                {weightError || weightUnitError}
              </FormHelperText>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              gap: '24px',
              flexWrap: 'wrap',
            }}
          >
            <ComplianceField
              excelRow={125}
              regulatoryFrameworks={values.regulatory_frameworks}
            >
              <StyledTextField
                style={{ flex: 1, minWidth: '140px' }}
                fullWidth
                type="text"
                label={t('dimensionLength')}
                name="dimensions_length"
                value={dimensionInputValues.length}
                onChange={(e) =>
                  handleDimensionsChange('length', e.target.value)
                }
                inputProps={{ inputMode: 'decimal' }}
              />
            </ComplianceField>
            <ComplianceField
              excelRow={126}
              regulatoryFrameworks={values.regulatory_frameworks}
            >
              <StyledTextField
                style={{ flex: 1, minWidth: '140px' }}
                fullWidth
                type="text"
                label={t('dimensionWidth')}
                name="dimensions_width"
                value={dimensionInputValues.width}
                onChange={(e) =>
                  handleDimensionsChange('width', e.target.value)
                }
                inputProps={{ inputMode: 'decimal' }}
              />
            </ComplianceField>
            <ComplianceField
              excelRow={127}
              regulatoryFrameworks={values.regulatory_frameworks}
            >
              <StyledTextField
                style={{ flex: 1, minWidth: '140px' }}
                fullWidth
                type="text"
                label={t('dimensionHeight')}
                name="dimensions_height"
                value={dimensionInputValues.height}
                onChange={(e) =>
                  handleDimensionsChange('height', e.target.value)
                }
                inputProps={{ inputMode: 'decimal' }}
              />
            </ComplianceField>
          </div>

          <ComplianceField
            excelRow={128}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <FormControl fullWidth error={!!dimensionsUnitError}>
              <InputLabel>{t('productDimensionsUnit')}</InputLabel>
              <Select
                value={values.dimensions_unit}
                label={t('productDimensionsUnit')}
                input={buildSelectInputWithInfo({
                  label: t('productDimensionsUnit'),
                  title: i18n.t('lca:fieldInfoFallback', {
                    field: t('productDimensionsUnit'),
                  }),
                })}
                onChange={(event) => handleChange(event, 'dimensions_unit')}
              >
                {dimensions_unit.map((el) => (
                  <MenuItem key={el.name} value={el.name}>
                    {el.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </ComplianceField>
          {(dimensionsError || dimensionsUnitError) && (
            <FormHelperText error>
              {dimensionsError || dimensionsUnitError}
            </FormHelperText>
          )}

          <div
            style={{
              display: 'flex',
              gap: '24px',
            }}
          >
            <ComplianceField
              excelRow={129}
              regulatoryFrameworks={values.regulatory_frameworks}
            >
              <NumberInput
                color="#7E4308"
                state={values}
                label={t('optionalVolume')}
                name="volume"
                value={values.volume}
                setValue={setValues}
                showInfoIcon
                infoText={i18n.t('lca:fieldInfoFallback', {
                  field: t('optionalVolume'),
                })}
              />
            </ComplianceField>
            <ComplianceField
              excelRow={130}
              regulatoryFrameworks={values.regulatory_frameworks}
            >
              <FormControl fullWidth>
                <InputLabel>{t('optionalVolumeUnit')}</InputLabel>
                <Select
                  value={values.volume_unit}
                  label={t('optionalVolumeUnit')}
                  input={buildSelectInputWithInfo({
                    label: t('optionalVolumeUnit'),
                    title: i18n.t('lca:fieldInfoFallback', {
                      field: t('optionalVolumeUnit'),
                    }),
                  })}
                  onChange={(event) => handleChange(event, 'volume_unit')}
                >
                  {volume_unit.map((el) => (
                    <MenuItem key={el.value} value={el.value}>
                      {el.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ComplianceField>
          </div>
        </>
      )}
    </div>
  );
}
